import type { ClientField } from '~/redux/api/unified-fields/enhancements';
import type { DataField } from '~/types/Field';
import type { PilotId } from '~/types/PilotId';

import { DataFieldType } from '~/enums/DataFieldType';
import { getFieldSourceDomain } from '~/features/unified-fields/field-manager/fieldManagerUtils';

function resolveDataFieldType(field: ClientField): DataFieldType | null {
  if (field.attributes.deactivated_at) {
    return null;
  }

  const {
    custom_settings,
    logical_type,
    option_set_id,
    suggestion_source_config,
  } = field.attributes;
  const hasOptionSet = Boolean(option_set_id);
  const isMulti = custom_settings?.allow_multiple_values === true;
  const canAddNewOptions = custom_settings?.allow_adding_new_options === true;

  switch (logical_type) {
    case 'TEXT':
      if (hasOptionSet) {
        return isMulti
          ? DataFieldType.ARRAY_MULTIPLE
          : DataFieldType.ARRAY_SINGLE;
      }
      if (canAddNewOptions || suggestion_source_config) {
        return null;
      }
      return DataFieldType.STRING;
    case 'NUMBER':
      return hasOptionSet ? null : DataFieldType.NUMBER;
    case 'DATE':
      return hasOptionSet ? null : DataFieldType.DATE;
    default:
      return null;
  }
}

export function mapUfClientFieldToDataField(
  field: ClientField,
): DataField | null {
  const type = resolveDataFieldType(field);
  if (!type) {
    return null;
  }

  const { api_name, custom_name, description } = field.attributes;
  const label = custom_name?.trim() || api_name?.trim();
  if (!label) {
    return null;
  }

  return {
    id: field.id as unknown as PilotId,
    label,
    type,
    helpText: description ?? null,
    isSmart: false,
    fieldSourceDomain: getFieldSourceDomain(field),
  };
}
